import React from 'react';
import { CLIENT_API } from 'api';
import './VideoPlayer.scss';

interface VideoPlayerProps {
  url: string;
  title?: string;
  width?: string;
  height?: string;
}

/**
 * Composant pour afficher une vidéo depuis différentes plateformes
 * Supporte : YouTube, Vimeo, et vidéos directes (mp4, webm, etc.)
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  url, 
  title = 'Vidéo', 
  width = '100%', 
  height = '400px' 
}) => {
  const normalizeUrl = (inputUrl: string): string => {
    if (!inputUrl) return inputUrl;
    if (/^https?:\/\//i.test(inputUrl)) return inputUrl;

    const base = CLIENT_API || '';
    if (!base) return inputUrl;
    if (inputUrl.startsWith('/')) return `${base}${inputUrl}`;

    return `${base}/${inputUrl}`;
  };

  // Fonction pour détecter le type de vidéo et générer l'embed approprié
  const getEmbedUrl = (url: string): { type: string; embedUrl: string } | null => {
    try {
      // YouTube
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const youtubeMatch = url.match(youtubeRegex);
      if (youtubeMatch) {
        return {
          type: 'youtube',
          embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
        };
      }

      // Vimeo
      const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
      const vimeoMatch = url.match(vimeoRegex);
      if (vimeoMatch) {
        return {
          type: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
        };
      }

      // Vidéo directe (mp4, webm, ogg)
      if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
        return {
          type: 'direct',
          embedUrl: url,
        };
      }

      // Fallback: beaucoup de previewUrl sont des URLs signées sans extension
      // (CloudFront/S3/CDN). Si c'est une URL http(s) et pas YouTube/Vimeo,
      // on tente quand même une lecture directe via <video>.
      if (/^https?:\/\//i.test(url)) {
        return {
          type: 'direct',
          embedUrl: url,
        };
      }

      return null;
    } catch (error) {
      console.error('Error parsing video URL:', error);
      return null;
    }
  };

  const normalizedUrl = normalizeUrl(url);
  const videoData = getEmbedUrl(normalizedUrl);

  if (!videoData) {
    return (
      <div className="video-player-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>URL de vidéo non valide ou format non supporté</p>
        <a href={normalizedUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
          Ouvrir dans un nouvel onglet
        </a>
      </div>
    );
  }

  if (videoData.type === 'direct') {
    return (
      <div className="video-player-container" style={{ height }}>
        <video 
          controls 
          style={{ width, height }}
          className="video-player-direct"
        >
          <source src={videoData.embedUrl} type="video/mp4" />
          <source src={videoData.embedUrl} type="video/webm" />
          <source src={videoData.embedUrl} type="video/ogg" />
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
      </div>
    );
  }

  return (
    <div className="video-player-container" style={{ height }}>
      <iframe
        src={videoData.embedUrl}
        title={title}
        width={width}
        height={height}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="video-player-iframe"
      />
    </div>
  );
};

export default VideoPlayer;
